import type { BookVariantStateV1, CustomerEngineEditionV1 } from "@lifescapsule/contracts";
import type { PrintBook } from "./print-book";

/** What the exact review reads from the book: its identity, never its signed cover URL. */
export type NativeReviewBook = Pick<PrintBook, "id" | "role" | "templateId">;

const nativeFamily = (templateId?: string) =>
  (
    ({ story: "MY_STORY", love: "OUR_STORY", recipes: "COOKBOOK", comic: "COMIC" }) as Record<
      string,
      string
    >
  )[templateId ?? ""];

/** The owner's approved edition is the book as it stands, with its print delivery named. */
function approvedEditionCurrent(
  state: BookVariantStateV1,
  book: NativeReviewBook,
  requestedEdition?: string | null,
) {
  const current = state.edition,
    proof = state.proof;
  const family = nativeFamily(book.templateId);
  return Boolean(
    book.role === "OWNER" &&
      family &&
      state.variant === family &&
      state.proofCurrent &&
      state.planCurrent &&
      state.blockers.length === 0 &&
      state.printApprovalByOwner === true &&
      current?.editionId &&
      state.bookRevision === current.bookRevision &&
      current.bookId === book.id &&
      proof?.bookId === book.id &&
      current.proofId === proof?.proofId &&
      current.bookRevision !== undefined &&
      (!requestedEdition || requestedEdition === current.editionId) &&
      state.delivery?.previewId === `variant-edition:${current.editionId}` &&
      state.delivery.bookRevision === current.bookRevision,
  );
}

export function exactNativeReview(
  state: BookVariantStateV1,
  edition: CustomerEngineEditionV1 | null,
  book: NativeReviewBook,
  requestedEdition?: string | null,
): boolean {
  const current = state.edition,
    proof = state.proof;
  const family = nativeFamily(book.templateId);
  return Boolean(
    approvedEditionCurrent(state, book, requestedEdition) &&
      current?.editionId &&
      edition?.bookId === book.id &&
      edition.family === family &&
      edition.view === "ACCEPTED" &&
      edition.immutable &&
      ["CAPTURED", "CAPTURED_READER_PENDING"].includes(edition.availability) &&
      edition.proofId === proof?.proofId &&
      edition.editionId === current.editionId &&
      Object.values(edition.currentness).every((value) => value === "CURRENT") &&
      proof?.interior.objectVersionId &&
      proof.cover.objectVersionId &&
      edition.files.some(
        (file) => file.assetId === "COVER" && file.checksumSha256 === proof.cover.checksumSha256,
      ) &&
      edition.files.some(
        (file) =>
          file.assetId === "INTERIOR" && file.checksumSha256 === proof.interior.checksumSha256,
      ),
  );
}

/**
 * An edition approved before retained captures began (28 October): no capture names its files,
 * so the exact review can't confirm them, but the book's own approved proof does, and that proof
 * is what its print package is built from (variant-print-source). The owner opens those files from
 * the proof's own links and prepares them for print again, then orders (A6, 2026-11-02). The
 * service re-checks everything when it builds the package and again when the order is placed.
 */
export function legacyNativeReview(
  state: BookVariantStateV1,
  edition: CustomerEngineEditionV1 | null,
  book: NativeReviewBook,
  requestedEdition?: string | null,
): boolean {
  return Boolean(
    approvedEditionCurrent(state, book, requestedEdition) &&
      edition?.bookId === book.id &&
      edition.availability === "LEGACY_READER_ONLY" &&
      state.proofUrls?.cover &&
      state.proofUrls.interior,
  );
}

/**
 * The retained capture can neither confirm nor rule out the approved files: an edition approved
 * before retained captures (LEGACY_READER_ONLY and the like) or a capture with a check it can't
 * make (UNKNOWN). The established print review answers for those books. A check known to be
 * STALE or MISSING is still the native review's answer: the book changed after approval.
 */
export function nativeEvidenceUnconfirmed(
  state: BookVariantStateV1,
  edition: CustomerEngineEditionV1 | null,
  book: NativeReviewBook,
): boolean {
  // Only a book its owner already approved can be "approved, files unconfirmable". A book never
  // approved (no kept edition) is not: it still needs its approval in Studio (review 2026-11-01).
  if (book.role !== "OWNER" || state.printApprovalByOwner !== true || !state.edition?.editionId)
    return false;
  if (!edition) return false;
  if (edition.availability === "LEGACY_READER_ONLY") return true;
  if (!["CAPTURED", "CAPTURED_READER_PENDING"].includes(edition.availability)) return false;
  const checks = Object.values(edition.currentness);
  return (
    checks.some((value) => value === "UNKNOWN" || value === "UNSUPPORTED") &&
    !checks.some((value) => value === "STALE" || value === "MISSING")
  );
}

/**
 * When a presigned link stops working, read from its own signature (X-Amz-Date + X-Amz-Expires);
 * a link whose signature can't be read is trusted only briefly after it was given (A6, review
 * P2-14). A page never counts an expired link as a file the customer opened.
 */
export function signedLinkExpiresAt(url: string, signedAt: number): number {
  try {
    const query = new URL(url).searchParams;
    const date = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/u.exec(
      query.get("X-Amz-Date") ?? "",
    );
    const seconds = Number(query.get("X-Amz-Expires"));
    if (date && Number.isInteger(seconds) && seconds > 0) {
      const [, year, month, day, hour, minute, second] = date.map(Number) as number[];
      return Date.UTC(year!, month! - 1, day!, hour!, minute!, second!) + seconds * 1000;
    }
  } catch {
    // An unreadable link falls back to the short window below.
  }
  return signedAt + 5 * 60_000;
}
